import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import {
  TaskStatuses,
  TaskStatusesInRestaurantPage,
} from './taskRouter.constants';

const selectTaskRouter = (state: RootState) => state.taskRouter;
const selectAgentStatus = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.agentStatus
);
const selectTasksById = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.tasksById
);
const selectTasksOrder = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.tasksOrder
);
const selectWSConnectionMessage = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.wsConnectionMessage
);
const selectTasksOffered = createSelector(
  selectTasksOrder,
  selectTasksById,
  (tasksOrder, tasksById) =>
    tasksOrder.filter((id) => tasksById[id].event === TaskStatuses.offered)
);
const selectActiveTaskOfferedId = createSelector(
  selectTasksOffered,
  (tasksOffered) => tasksOffered?.[0]
);
const selectCurrentTask = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.currentTaskId
);
const selectCurrentTaskRestaurantCode = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.currentTaskRestaurantCode
);
const selectCurrentTaskRestaurantName = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.currentTaskRestaurantName
);
const selectCurrentTaskStatus = createSelector(
  selectCurrentTask,
  selectTasksById,
  (currentTaskId, tasksById) => tasksById?.[currentTaskId]?.event
);
const selectCurrentTaskRestaurantData = createSelector(
  selectCurrentTask,
  selectTasksById,
  (currentTaskId, tasksById) =>
    tasksById?.[currentTaskId]?.data?.restaurant_data
);
const selectIsCurrentTaskAIEnabled = createSelector(
  selectCurrentTaskRestaurantData,
  (restaurantData) => restaurantData?.ai_enabled
);
const selectCurrentTaskAIStatus = createSelector(
  selectCurrentTask,
  selectTasksById,
  (currentTaskId, tasksById) => tasksById?.[currentTaskId]?.data?.ai_status
);
const selectTaskAssignedSessionId = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.taskAssignedSessionId
);
const selectTaskAssignedLaneId = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.taskAssignedLaneId
);
const selectTaskAssignedSessionStartedAt = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.taskAssignedSessionStartedAt
);
const selectIsExpressTimerElapsed = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.isExpressTimerElapsed
);
const selectTasksAssigned = createSelector(
  selectTasksOrder,
  selectTasksById,
  (tasksOrder, tasksById) =>
    tasksOrder.filter((id) => tasksById[id].event === TaskStatuses.assigned)
);
const selectAssignedTaskId = createSelector(
  selectTasksAssigned,
  (tasksAssigned) => tasksAssigned?.[0]
);

const selectTaskStatusChanged = createSelector(
  selectTasksById,
  selectCurrentTask,
  (tasksById, currentTaskId) => {
    if (tasksById && currentTaskId) {
      const task = tasksById[currentTaskId];
      if (
        task &&
        [
          TaskStatuses.accepted,
          TaskStatuses.invalid,
          TaskStatuses.invalidAck,
          ...TaskStatusesInRestaurantPage,
        ].includes(task?.event)
      ) {
        return task;
      }
    }
  }
);

const selectNavigationViaTaskRouter = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.navigationViaTaskRouter
);

const selectAgentInSession = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.agentInSession
);

const selectIsKillSwitchClicked = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.isKillSwitchClicked
);

const selectIsCurrentTaskUnsupervised = createSelector(
  selectTaskRouter,
  (taskRouter) => taskRouter.isCurrentTaskUnsupervised
);

export {
  selectActiveTaskOfferedId,
  selectAgentInSession,
  selectAgentStatus,
  selectAssignedTaskId,
  selectCurrentTask,
  selectCurrentTaskAIStatus,
  selectCurrentTaskRestaurantCode,
  selectCurrentTaskRestaurantName,
  selectCurrentTaskStatus,
  selectIsCurrentTaskAIEnabled,
  selectNavigationViaTaskRouter,
  selectTaskAssignedLaneId,
  selectTaskAssignedSessionId,
  selectTaskAssignedSessionStartedAt,
  selectIsExpressTimerElapsed,
  selectTaskRouter,
  selectTaskStatusChanged,
  selectTasksAssigned,
  selectTasksById,
  selectTasksOffered,
  selectTasksOrder,
  selectWSConnectionMessage,
  selectIsKillSwitchClicked,
  selectIsCurrentTaskUnsupervised,
};
